import React, { useCallback, useMemo, useState } from 'react';

import { EmailValidator } from 'commons-validator-js';

import { ButtonPriority, ButtonSize } from '@wix/wix-ui-tpa';
import { Button, TextArea, TextField } from '@wix/wix-ui-tpa/cssVars';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { selectOneChatSettings, submitVisitorAsContact } from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { isPhoneValidSimplified } from '@/utils';

import { ChatCard } from '../ChatCard';

import { st, classes } from './ContactForm.st.css';
import { ContactFormDataHooks } from './ContactFormDataHooks';

export interface FormData {
  email?: string;
  name?: string;
  phone?: string;
  message?: string;
  formId: string;
}

type ContactFormProps = {
  formId: string;
};

export const ContactForm = ({ formId }: ContactFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isEditor } = useEnvironment();
  const contactFormFields = useAppSelector(
    selectOneChatSettings.contactFormFields,
  );

  const [formData, setFormData] = useState<FormData>({
    email:
      contactFormFields.includeEmail && isEditor ? 'john.doe@example.com' : '',
    formId,
  });
  const emailValidator = useMemo(() => new EmailValidator(), []);
  const isEmailValid = useMemo(
    () => emailValidator.isValid(formData.email ?? ''),
    [formData.email, emailValidator],
  );
  const isPhoneValid = useMemo(
    () => isPhoneValidSimplified(formData.phone),
    [formData.phone],
  );
  const isRequiredNameValid = !contactFormFields.requiredName
    ? true
    : Boolean(formData.name?.trim());
  const isRequiredEmailValid = contactFormFields.requiredEmail
    ? Boolean(formData.email) && isEmailValid
    : !formData.email || isEmailValid;
  const isRequiredPhoneValid = contactFormFields.requiredPhone
    ? Boolean(formData.phone) && isPhoneValid
    : !formData.phone || isPhoneValid;
  const isRequiredMessageValid = !contactFormFields.requiredMessage
    ? true
    : Boolean(formData.message?.trim());
  const canSubmit =
    isRequiredNameValid &&
    isRequiredEmailValid &&
    isRequiredPhoneValid &&
    isRequiredMessageValid;

  const onSubmit = useCallback(
    (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      dispatch(submitVisitorAsContact(formData));
    },
    [dispatch, formData],
  );

  return (
    <ChatCard
      className={st(classes.root)}
      cardClassName={st(classes.card)}
      data-hook={ContactFormDataHooks.ROOT}
    >
      <form onSubmit={onSubmit}>
        {contactFormFields.includeName && (
          <>
            <TextField
              className={st(classes.input)}
              label={t('app.widget.form.name')}
              required={contactFormFields.requiredName}
              value={formData.name}
              onChange={(ev) =>
                setFormData({ ...formData, name: ev.target.value })
              }
            />
            <br />
          </>
        )}

        {contactFormFields.includeEmail && (
          <TextField
            className={st(classes.input)}
            label={t('app.widget.form.email')}
            value={formData.email ?? ''}
            onChange={(ev) =>
              setFormData({ ...formData, email: ev.target.value })
            }
            newErrorMessage
            error={Boolean(formData.email) && !isEmailValid}
            errorMessage={t('app.widget.form.email-validation-error')}
            required={contactFormFields.requiredEmail}
          />
        )}

        {contactFormFields.includePhone && (
          <>
            <br />
            <TextField
              className={st(classes.input)}
              label={t('app.widget.form.phone')}
              required={contactFormFields.requiredPhone}
              value={formData.phone}
              onChange={(ev) =>
                setFormData({ ...formData, phone: ev.target.value })
              }
              newErrorMessage
              error={Boolean(formData.phone) && !isPhoneValid}
              errorMessage={t('app.widget.form.phone-validation-error')}
              type="tel"
            />
          </>
        )}

        {contactFormFields.includeMessage && (
          <>
            <br />
            <TextArea
              className={st(classes.textArea)}
              label={t('app.widget.form.message')}
              required={contactFormFields.requiredMessage}
              value={formData.message ?? ''}
              ariaLabel="test"
              onChange={(ev) =>
                setFormData({ ...formData, message: ev.target.value })
              }
            />
          </>
        )}

        <br />
        <Button
          type="submit"
          className={st(classes.button, { disabled: !canSubmit })}
          upgrade
          fullWidth
          priority={ButtonPriority.primary}
          disabled={!canSubmit}
          size={ButtonSize.tiny}
        >
          {t('app.widget.form.submit')}
        </Button>
      </form>
    </ChatCard>
  );
};
